import HeatmapOverlay from 'leaflet-heatmap'
import radius from './bloomRadiusCalculator'
import airbrake from '@/helpers/airbrake'

export default class HeatmapLayer {
  constructor (cfg = {}) {
    Object.assign(this, cfg)
    const lMap = this.lMap
    this.mapBounds = this.currentMapBounds()
    this.overlay = new HeatmapOverlay(this.getConfig())
    this.loaded = false

    /**
     * Certain types of heatmaps have flat data where the values are very close to each other
     * like in case of average payments. Such data sets may require smaller bloom radii for an
     * appealing UX. While the radiusModifier config option is not a bullet-proof solution it's
     * a reasonable attempt to *fix* such cases.
     *
     * TODO: data from such APIs should not be squeezed on the server side either or the transformation algorithm should be rethought.
     */
    this.radiusModifier = this.radiusModifier || 1
    this.defaultZoom = lMap.getZoom()

    lMap
      .on('zoomend', () => {
        if (this.overlay._heatmap) { // _heatmap is undefined if the layer was not displayed yet
          this.setHeatmapBloomRadius()
          this.overlay._draw()
        }
      }).on('moveend', () => { // 'moveend' is always fired after a 'zoomend' event thus there is no need to run mapBoundsChanged() after 'zoomend'
        this.mapBoundsChanged()
      }).on('layeradd', e => { // called when it's added to the map and also when the user selects it from the layers control
        if (e.layer && e.layer.cfg && e.layer.cfg.id === this.id && this.isVisible()) { // this instance got visible
          this.$store.dispatch('heatmap/setSelectedHeatmapId', this.id)
          if (!this.loaded) {
            this.loadData()
          }
        }
      })

    this.heatmap.dispatchMapBoundsChange = true
    this.bus.$on('reset-map-height', () => {
      // temporarily disable emitting map-bounds-changed events to avoid displaying the refresh button when
      // the window is resized. Fix for DOHA-1224 (Mobile: "refresh data" appears though user has made no changes to heatmap)
      this.heatmap.dispatchMapBoundsChange = false
    })
  }

  refresh () {
    this.data = {max: 100, data: []} // clear all heatmaps
    this.loaded = false
    // A snapshot of the heatmap's current bounds. This may change as the user interacts
    // with the map but we want to use the snapshot when loading data for the layers
    this.mapBounds = this.$store.state.heatmap.mapBounds
    this.defaultZoom = this.lMap.getZoom()
    if (this.isVisible()) {
      this.loadData()
    } else {
      this.overlay.setData(this.data)
    }
  }

  /**
   * Save the current map bounds in a vuex store that'll trigger displaying a refresh link
   */
  mapBoundsChanged () {
    // Only visible heatmaps trigger the event.
    if (this.isVisible()) {
      if (!this.heatmap.dispatchMapBoundsChange) {
        this.heatmap.dispatchMapBoundsChange = true
        return
      }
      this.$store.dispatch('heatmap/setMapBounds', {
        mapBounds: this.currentMapBounds(),
        mapBoundsChangedByUser: true
      })
    }
  }

  isVisible () {
    return this.lMap.hasLayer(this.overlay)
  }

  /**
   * @return String The map bounds as a string ready to be used in API calls
   */
  currentMapBounds () {
    return this.lMap.getBounds().toBBoxString().replace(/\./g, '_')
  }

  loadData () {
    this.$store.dispatch('heatmap/setHeatmapState', 'LOADING')

    this
      .getData()
      .then(response => {
        // TODO status code may be 3xx, 4xx or 5xx that should not be considered success
        this.data = response.data
        this.setHeatmapBloomRadius()
        this.overlay.setData(this.data)
        this.loaded = true
        /*
        Commented out for now because it complicates the logic and it can easily mess up heatmap visuals.
        TODO: The map_zoom data could be used to calculate the pixel dimensions of the rectangle enclosing
        TODO: the data points, which in turn should be used to modify the bloom radius.
        if (this.data.map_zoom && this.data.data.length > 1){
          // map_zoom is the enclosing rectangle of the geo data from the server. It may be smaller than the actual size
          // of the requested geo depending on data availability in Elastic search.
          // When a single data point is returned from doha we avoid flying to it because it often does not
          // match the location of the geo. E.g. it could result in focusing on a nearby forrest of a town. Looks like
          // there are geo data issues in the doha DB.
          this.lMap.flyToBounds(this.data.map_zoom, {
            // The stats panel's max width is around 300px. The 270px padding here ensures the selected geo
            // does not or just slightly overlaps with the stats panel.
            paddingTopLeft: [285, 0]
          })
        }
        */
        this.$store.dispatch('heatmap/setHeatmapState', 'LOADED')
      })
      .catch(err => {
        this.$store.dispatch('heatmap/setHeatmapState', 'FAILED')
        airbrake.notify(err)
      })
  }

  /**
   * adjust the bloom radius depending on zoom level, heatmap type and data size
   */
  setHeatmapBloomRadius () {
    const relativeZoomLevel = this.lMap.getZoom() - this.defaultZoom
    this.overlay.cfg.radius = radius(relativeZoomLevel, this.data ? this.data.data.length : 0, this.radiusModifier)
  }

  getConfig () {
    return {
      id: this.id,
      // radius should be small ONLY if scaleRadius is true (or small radius is intended)
      'radius': radius(0),
      // scales the radius based on map zoom, does not let drill down to details. Not a great option for us
      'scaleRadius': false,
      'maxOpacity': 0.45,
      // if set to false the heatmap uses the global maximum for colorization
      // if activated: uses the data maximum within the current map boundaries
      //   (there will always be a red spot with useLocalExtremas true)
      'useLocalExtrema': false,
      latField: 'lat',
      lngField: 'lon',
      valueField: 'count',
      // gradient: {"0.45": "#6a8dd4", "0.55": "#516ba3", "0.65": "#90c47c", "0.95": "#294580", "1.0": "#ffcc00"}

      // some others to consider:
      // gradient: {'0': 'Red', '0.85': 'Yellow', '1': 'White'},
      // gradient: {"0.45": "#6a8dd4", "0.55": "#516ba3", "0.65": "#90c47c", "0.95": "#294580", "1.0": "#ffcc00"}
      // gradient: {"0.45": "#6a8dd4", "0.55": "#516ba3", "0.65": "#90c47c", "0.95": "#294580", "1.0": "#FF8C6D"}
      // gradient: {"0.45": "#6a8dd4", "0.55": "#516ba3", "0.65": "#90c47c", "0.95": "#294580", "1.0": "#F2FF98"}
      // gradient: {"0.45": "#6a8dd4", "0.55": "#516ba3", "0.65": "#90c47c", "0.95": "#294580", "1.0": "#FF8400"}

      /*
       gradient: { // for black maps
         "0.00": "rgb(42, 101, 130)",
         "0.08370": "rgb(42, 101, 130)",
         "0.08620": "rgb(69, 148, 188)",
         "0.27420": "rgb(69, 148, 188)",
         "0.27660": "rgb(111, 193, 232)",
         "0.63210": "rgb(111, 193, 232)",
         "0.63700": "rgb(185, 225, 244)",
         "0.81470": "rgb(185, 225, 244)",
         "0.81710": "rgba(185, 225, 244, 0.987)",
         "0.82230": "rgba(43, 43, 43, 0.959)",
         "1.0": "rgba(43, 43, 43, 0.000)"
       }
       */
      /*
       gradient: { // starts with red. Does not look good
         "0.00": "rgb(241, 42, 42)",
         "0.9130": "rgb(241, 42, 42)",
         "0.9131": "rgb(241, 100, 42)",
         "0.25630": "rgb(241, 100, 42)",
         "0.26150": "rgb(255, 252, 0)",
         "0.62430": "rgb(255, 252, 0)",
         "0.62940": "rgb(140, 220, 0)",
         "0.79440": "rgb(140, 220, 0)",
         "0.80470": "rgb(145, 221, 12)",
         "1.0": "rgba(255, 255, 255, 0.000)"
       }
       */
      gradient: {
        '0.10': 'rgb(1, 92, 74)',
        '0.18620': 'rgb(1, 92, 74)',
        '0.18890': 'rgb(31, 149, 126)',
        '0.36660': 'rgb(31, 149, 126)',
        '0.46900': 'rgb(72, 201, 176)',
        '0.69400': 'rgb(72, 201, 176)',
        '0.69890': 'rgb(145, 241, 222)',
        '0.95230': 'rgb(145, 241, 222)',
        '0.95710': 'rgb(211, 255, 246)',
        '0.98740': 'rgb(211, 255, 246)',
        '1.0': 'rgba(211, 255, 246, .1)'
      }
    }
  }
}



// WEBPACK FOOTER //
// ./frontend/src/components/heatmap/HeatmapLayer.js